import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Mail, Send, FileText, AlertCircle, CheckCircle, XCircle, RefreshCw, User, MapPin, Briefcase } from 'lucide-react';
import { colors, detailStyles } from './detail.styles';
/**
 * Map batch status to a display color
 */
function getBatchStatusColor(status) {
    switch (status) {
        case 'created':
            return colors.accent.green;
        case 'sent':
            return colors.accent.blue;
        case 'failed':
            return colors.accent.red;
        case 'resent':
            return colors.accent.amber;
        default:
            return colors.text.secondary;
    }
}
/**
 * Get the appropriate icon for a batch status
 */
function getBatchStatusIcon(status) {
    switch (status) {
        case 'created':
            return _jsx(CheckCircle, { size: 14, style: { color: getBatchStatusColor(status) } });
        case 'sent':
            return _jsx(Send, { size: 14, style: { color: getBatchStatusColor(status) } });
        case 'failed':
            return _jsx(XCircle, { size: 14, style: { color: getBatchStatusColor(status) } });
        case 'resent':
            return _jsx(RefreshCw, { size: 14, style: { color: getBatchStatusColor(status) } });
        default:
            return _jsx(AlertCircle, { size: 14, style: { color: getBatchStatusColor(status) } });
    }
}
/**
 * Format the batch status for display
 */
function formatBatchStatus(status) {
    return status.charAt(0).toUpperCase() + status.slice(1);
}
/**
 * Derive batch status from the activity type.
 * Example: 'invoice_batch.sent' -> 'sent'
 */
function deriveBatchStatus(activityType) {
    const parts = activityType.split('.');
    return parts.length > 1 ? parts[parts.length - 1] : 'unknown';
}
export const InvoiceBatchDetail = ({ activity, metadata, onUserClick, onWorkOrderClick }) => {
    const displayId = metadata.display_id;
    const invoiceCount = metadata.invoice_count;
    const recipientEmail = metadata.recipient_email;
    const errorMessage = metadata.error_message;
    // New enriched metadata fields
    const sentByName = metadata.sent_by_name;
    const sentByUserId = metadata.sent_by_user_id;
    const locationName = metadata.location_name;
    const workOrderIds = metadata.work_order_ids || [];
    const workOrderDisplayIds = metadata.work_order_display_ids || [];
    const recipientNames = metadata.recipient_names || [];
    const recipientUserIds = metadata.recipient_user_ids || [];
    const externalEmails = metadata.external_emails || [];
    const batchStatus = deriveBatchStatus(activity.activity_type);
    const statusColor = getBatchStatusColor(batchStatus);
    // Build a unified list of all recipients for display.
    // Internal users come from recipient_names/recipient_user_ids (positionally matched).
    // External emails come from external_emails.
    // Falls back to legacy recipient_email if no structured data is available.
    const hasStructuredRecipients = recipientNames.length > 0 || externalEmails.length > 0;
    // Count totals for the summary
    const totalRecipients = hasStructuredRecipients
        ? recipientNames.length + externalEmails.filter((e) => !recipientNames.includes(e)).length
        : recipientEmail
            ? 1
            : 0;
    return (_jsxs("div", { style: detailStyles.detailContent, children: [_jsxs("div", { style: detailStyles.sectionHeader, children: [_jsx(Send, { size: 16, style: { color: statusColor } }), _jsx("span", { children: "Invoice Details" })] }), _jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 12px',
                    backgroundColor: colors.background.secondary,
                    borderRadius: '6px',
                    borderLeft: `3px solid ${statusColor}`,
                }, children: [getBatchStatusIcon(batchStatus), _jsx("span", { style: {
                            fontSize: '13px',
                            fontWeight: 600,
                            color: statusColor,
                        }, children: formatBatchStatus(batchStatus) }), displayId && (_jsx("span", { style: {
                            marginLeft: 'auto',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            color: colors.text.secondary,
                        }, children: displayId }))] }), _jsxs("div", { children: [sentByName && (_jsxs("div", { style: detailStyles.infoRow, children: [_jsx(User, { size: 14, style: { color: colors.text.secondary } }), _jsx("span", { style: detailStyles.infoLabel, children: "Sent By:" }), _jsx("span", { style: sentByUserId && onUserClick ? { ...detailStyles.infoValue, ...detailStyles.linkText } : detailStyles.infoValue, onClick: () => sentByUserId && onUserClick?.(sentByUserId), children: sentByName })] })), locationName && (_jsxs("div", { style: detailStyles.infoRow, children: [_jsx(MapPin, { size: 14, style: { color: colors.text.secondary } }), _jsx("span", { style: detailStyles.infoLabel, children: "Location:" }), _jsx("span", { style: detailStyles.infoValue, children: locationName })] })), invoiceCount !== undefined && invoiceCount !== null && (_jsxs("div", { style: detailStyles.infoRow, children: [_jsx(FileText, { size: 14, style: { color: colors.text.secondary } }), _jsx("span", { style: detailStyles.infoLabel, children: "Invoices:" }), _jsxs("span", { style: detailStyles.infoValue, children: [invoiceCount, " invoice", invoiceCount !== 1 ? 's' : ''] })] }))] }), (workOrderDisplayIds.length > 0 || (invoiceCount && workOrderDisplayIds.length === 0)) && (_jsxs("div", { children: [_jsxs("div", { style: detailStyles.sectionHeader, children: [_jsx(Briefcase, { size: 16, style: { color: colors.accent.cyan } }), _jsx("span", { children: "Work Orders" })] }), workOrderDisplayIds.length > 0 ? (_jsx("div", { style: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                        }, children: workOrderDisplayIds.map((woDisplayId, index) => {
                            const woId = index < workOrderIds.length ? workOrderIds[index] : null;
                            const isClickable = woId && onWorkOrderClick;
                            return (_jsxs("span", { style: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '4px 10px',
                                    backgroundColor: colors.background.tertiary,
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    color: isClickable ? colors.accent.blue : colors.text.primary,
                                    cursor: isClickable ? 'pointer' : 'default',
                                    border: `1px solid ${colors.border.default}`,
                                }, onClick: () => woId && onWorkOrderClick?.(woId), children: [_jsx(FileText, { size: 12, style: { color: isClickable ? colors.accent.blue : colors.text.tertiary } }), woDisplayId] }, index));
                        }) })) : (_jsxs("div", { style: {
                            fontSize: '12px',
                            color: colors.text.secondary,
                            padding: '8px 12px',
                            backgroundColor: colors.background.secondary,
                            borderRadius: '6px',
                        }, children: [invoiceCount, " invoice", invoiceCount !== 1 ? 's' : ''] }))] })), (hasStructuredRecipients || recipientEmail) && (_jsxs("div", { children: [_jsxs("div", { style: detailStyles.sectionHeader, children: [_jsx(Mail, { size: 16, style: { color: colors.accent.purple } }), _jsx("span", { children: "Recipients" })] }), _jsx("div", { style: detailStyles.recipientList, children: hasStructuredRecipients ? (_jsxs(_Fragment, { children: [recipientNames.map((name, index) => {
                                    const isInternal = index < recipientUserIds.length;
                                    const userId = isInternal ? recipientUserIds[index] : null;
                                    const isClickable = userId && onUserClick;
                                    return (_jsxs("div", { style: detailStyles.recipientItem, children: [isInternal ? (_jsx(User, { size: 14, style: { color: colors.accent.cyan } })) : (_jsx(Mail, { size: 14, style: { color: colors.accent.purple } })), _jsx("span", { style: isClickable ? { ...detailStyles.recipientName, ...detailStyles.linkText } : detailStyles.recipientName, onClick: () => userId && onUserClick?.(userId), children: name }), _jsx("span", { style: detailStyles.recipientType, children: isInternal ? 'User' : 'External' })] }, `user-${index}`));
                                }), externalEmails
                                    .filter((email) => !recipientNames.includes(email))
                                    .map((email, index) => (_jsxs("div", { style: detailStyles.recipientItem, children: [_jsx(Mail, { size: 14, style: { color: colors.accent.purple } }), _jsx("span", { style: detailStyles.recipientName, children: email }), _jsx("span", { style: detailStyles.recipientType, children: "External" })] }, `ext-${index}`)))] })) : (_jsxs("div", { style: detailStyles.recipientItem, children: [_jsx(Mail, { size: 14, style: { color: colors.accent.purple } }), _jsx("span", { style: detailStyles.recipientName, children: recipientEmail })] })) })] })), errorMessage && (_jsxs("div", { style: {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                }, children: [_jsxs("div", { style: detailStyles.sectionHeader, children: [_jsx(AlertCircle, { size: 16, style: { color: colors.accent.red } }), _jsx("span", { children: "Error Details" })] }), _jsx("div", { style: {
                            backgroundColor: colors.background.secondary,
                            borderRadius: '6px',
                            padding: '12px',
                            fontSize: '12px',
                            color: colors.accent.red,
                            lineHeight: 1.5,
                            borderLeft: `3px solid ${colors.accent.red}`,
                            wordBreak: 'break-word',
                        }, children: errorMessage })] })), _jsxs("div", { style: detailStyles.summaryRow, children: [_jsx(Send, { size: 14, style: { color: colors.text.secondary } }), _jsxs("span", { style: detailStyles.summaryText, children: [formatBatchStatus(batchStatus), " invoice covering ", invoiceCount ?? '?', " work order", invoiceCount !== 1 ? 's' : '', totalRecipients > 0 ? ` to ${totalRecipients} recipient${totalRecipients !== 1 ? 's' : ''}` : ''] })] })] }));
};
export default InvoiceBatchDetail;
